import { FieldLabel, Text } from 'common-components/typography';
import { HyperlinkButton } from 'common-components/buttons';
import { Col, Icon, Row } from 'antd';
import { Tooltip2 } from '@blueprintjs/popover2';
import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { Text as IconText, Icons, Inline } from '@goodhuman-me/components';
import { ServiceAgreementSigningStatus, ServiceAgreementStatus, ServiceType } from 'utilities/enum-utils';
import { IServiceAgreement, IServiceAgreementService } from 'interfaces/customer-interfaces';
import { AgreementStatusTag } from 'views/customers/details/tabs-panel/new-service-agreements/agreements-listing/AgreementStatusTag';
import _ from 'lodash';
import CommonUtils from 'utilities/common-utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CreateAgreementLinkButton } from './create-agreement-link/create-agreement-link-button';
import { Group, Paper, Button as MantineButton, Divider } from '@good/ui/core';
import { FileAgreement, FileDownload, History, Trash } from '@good/icons';
import { DownloadPdfButton } from './download-pdf-button';

interface IServiceAgreementDetailsProps {
  item: IServiceAgreement;
  onClickActionModal?: (item, action, value) => void;
  openViewModal: (selectedServiceLineItems) => void;
  onClickViewVersionHistory?: any;
  isHistory?: boolean;
  companyData: any;
  hasEditPermission: boolean;
  customerId: string;
  className?: string;
}

export function ServiceAgreementDetails({
  item,
  customerId,
  onClickActionModal,
  openViewModal,
  onClickViewVersionHistory,
  isHistory,
  hasEditPermission,
  companyData,
  className = 'mt-x-large',
}: IServiceAgreementDetailsProps): JSX.Element {
  const { al927: datesOnUserServicesFeature } = useFlags();
  let { t } = useTranslation('', { keyPrefix: 'warningMessages' });
  let estimatedDiff: any = 0;
  if (item.budgetTracking && !isHistory) {
    estimatedDiff = _.sumBy(item.budgetTracking, (item: any) => Number(item.estimatedDifference));
  }
  let totalQuoteAmount = 0;
  let totalAmountSpent = 0;
  let totalForecastedSpend = 0;
  _.forEach(item.budgetTracking, (tracking) => {
    totalQuoteAmount = Number(totalQuoteAmount) + Number(tracking.quoteAmount);
    totalAmountSpent = Number(totalAmountSpent) + Number(tracking.spentAmount) + Number(tracking.amountSpent);
    totalForecastedSpend = Number(totalForecastedSpend) + Number(tracking.forecastedSpend);
  });

  const [serviceLineItemsArray, setserviceLineItemsArray] = useState([]);

  //calculating length of service items to display
  useEffect(() => {
    let lengthArray = [];
    if (item && item.services) {
      lengthArray = _.map(item.services, (service) => {
        if (service.additionalCharges) {
          const uniqueArray = _.uniqBy(service.additionalCharges.autoChargedBillingItems, 'supportItemNumber');
          let totalLength = service.lineItems.length + uniqueArray.length;
          _.forEach(uniqueArray, (autoChargeLineItem) => {
            if (
              _.find(service.lineItems, (lineItem) => {
                return lineItem.supportItemNumber === autoChargeLineItem.supportItemNumber;
              })
            ) {
              totalLength--;
            }
          });
          return totalLength;
        }
      });
    }
    setserviceLineItemsArray(lengthArray);
  }, [item]);

  const expiredWarningIcon = (): JSX.Element => {
    return (
      <Tooltip2 content={t('expired')} position='top'>
        <IconText color='$critical' size='large'>
          <Icons.Critical width='$large' height='$large' color='#BA2524' />
        </IconText>
      </Tooltip2>
    );
  };

  const expiresSoonWarningIcon = (): JSX.Element => {
    return (
      <Tooltip2 content={t('expiringSoon')} position='top'>
        <IconText color='$notice' size='small'>
          <Icons.Critical width='$large' height='$large' />
        </IconText>
      </Tooltip2>
    );
  };

  const displayExpirationWarnings = (
    status: IServiceAgreementService['serviceExpirationStatus'],
  ): JSX.Element | null => {
    return {
      NEARING_EXPIRATION: expiresSoonWarningIcon(),
      EXPIRED: expiredWarningIcon(),
    }[status];
  };

  const colSpans = datesOnUserServicesFeature
    ? {
        serviceNames: 5,
        lineItems: 4,
        comments: 5,
      }
    : {
        serviceNames: 6,
        lineItems: 6,
        comments: 8,
      };

  return (
    <div className={className}>
      {!isHistory && (
        <div className='mb-x-large'>
          {/* Content Box */}
          <div className='bordered p-medium rounded'>
            <FieldLabel text='STATUS' />

            {/* Status Tag */}
            <AgreementStatusTag status={item.status} />
            <Text>
              {item.signedStatus === ServiceAgreementSigningStatus.SIGNED ? (
                <div>
                  <Text>
                    This service agreement has been <b>signed</b>
                  </Text>
                </div>
              ) : (
                <div>
                  <Text>
                    This service agreement is <b>unsigned</b>
                  </Text>
                </div>
              )}
            </Text>
          </div>
        </div>
      )}
      <div className='mb-x-large'>
        {/* Label */}
        <div className='pb-small'>
          <Text size='x-large' weight='bold'>
            General Information
          </Text>
        </div>

        {/* Content Box */}
        <div className='bordered p-medium rounded'>
          <Row gutter={16}>
            <Col span={7}>
              <FieldLabel text='START DATE' />
              <Text size='x-large'>
                {item.startDate ? (
                  moment.tz(item.startDate, companyData.timezone).format('DD MMM YYYY')
                ) : (
                  <Text color={'secondary'}>Not set.</Text>
                )}
              </Text>
            </Col>
            <Col span={7}>
              <FieldLabel text='END DATE' />
              <Text size='x-large'>
                {item.endDate ? (
                  moment.tz(item.endDate, companyData.timezone).format('DD MMM YYYY')
                ) : (
                  <Text color={'secondary'}>Not set.</Text>
                )}
              </Text>
            </Col>

            <Col span={7}>
              {item.signedStatus === ServiceAgreementSigningStatus.SIGNED && (
                <>
                  <FieldLabel text='SIGNED ON' />
                  <Text size='x-large'>{moment(item.signedDate).format('DD MMM YYYY')}</Text>
                </>
              )}
            </Col>
            {!isHistory && hasEditPermission && (
              <Col span={3} className={'text-align-right'}>
                <HyperlinkButton onClick={() => onClickActionModal(item, 'DATES', null)}>
                  <Icon type='edit' /> Modify...
                </HyperlinkButton>
              </Col>
            )}
          </Row>
        </div>
      </div>
      {/* Terms & Conditions*/}
      <div className='mb-x-large'>
        {/* Label */}
        <div className='pb-small'>
          <Text weight='bold'>Terms & Conditions</Text>
        </div>

        {/* Content Box */}
        <div className='bordered p-medium rounded' style={{ maxHeight: '200px', overflowY: 'auto' }}>
          <div className='flex-row justify-between'>
            <FieldLabel text='TERMS & CONDITIONS' />
            {!isHistory && hasEditPermission && (
              <HyperlinkButton onClick={() => onClickActionModal(item, 'TERMS_CONDITIONS', null)}>
                <Icon type='edit' /> Modify...
              </HyperlinkButton>
            )}
          </div>
          {item.termsConditions ? (
            <div
              className='mt-small'
              dangerouslySetInnerHTML={{
                __html: item.termsConditions ? item.termsConditions : '',
              }}
            />
          ) : (
            <Text color={'secondary'}>No Terms and Conditions set.</Text>
          )}
        </div>
      </div>
      {/* Schedule of Support(s)*/}
      <div className='mb-x-large'>
        <div className='pb-small'>
          <Text weight='bold'>Services being delivered</Text>
        </div>

        {/* Content Box */}
        <div className='bordered p-medium rounded'>
          <div className='pb-small flex-row justify-between'>
            <FieldLabel text='SERVICE(S)' />
            {!isHistory && hasEditPermission && (
              <HyperlinkButton onClick={() => onClickActionModal(item, 'SERVICE_ITEMS', null)}>
                <Icon type='edit' /> Modify...
              </HyperlinkButton>
            )}
          </div>

          {/* Header */}
          <div className='bordered-bottom pb-medium'>
            <Row gutter={8}>
              <Col span={colSpans.serviceNames}>
                <div className='ph-medium'>
                  <FieldLabel text={'SERVICE NAME'} />
                </div>
              </Col>
              <Col span={colSpans.lineItems}>
                <div className='ph-medium'>
                  <FieldLabel text={'LINE ITEM(S)'} />
                </div>
              </Col>
              {datesOnUserServicesFeature && (
                <Col span={6}>
                  <div className='ph-medium'>
                    <FieldLabel text={'DATES'} />
                  </div>
                </Col>
              )}
              <Col span={colSpans.comments}>
                <div className='ph-medium'>
                  <FieldLabel text={'COMMENTS'} />
                </div>
              </Col>
            </Row>
          </div>

          {/* Item */}
          {item.services &&
            item.services.map((service, index) => (
              <div key={index} className='pv-medium evenodd'>
                <Row gutter={16}>
                  <Col span={colSpans.serviceNames}>
                    <div className='ph-medium'>{service.serviceName}</div>
                  </Col>
                  <Col span={colSpans.lineItems}>
                    <div className='ph-medium'>
                      {service.lineItems && service.lineItems.length > 0 ? (
                        <Text>
                          {serviceLineItemsArray[index] ? serviceLineItemsArray[index] : service.lineItems.length} line
                          item
                          {service.lineItems.length !== 1 ? 's' : ''}
                        </Text>
                      ) : (
                        '0 line items'
                      )}
                    </div>
                  </Col>
                  {datesOnUserServicesFeature && (
                    <Col span={6}>
                      <Inline gap='$xsmall'>
                        <div className='ph-small'>
                          {`${moment
                            .tz(service.startDate ?? item.startDate, companyData.timezone)
                            .format('DD/MM/YYYY')} - ${moment
                            .tz(service.endDate ?? item.endDate, companyData.timezone)
                            .format('DD/MM/YYYY')}`}
                        </div>
                        {displayExpirationWarnings(service.serviceExpirationStatus)}
                      </Inline>
                    </Col>
                  )}
                  <Col span={colSpans.comments}>
                    <div className='ph-medium'>{service.comment} </div>
                  </Col>
                  <Col span={4}>
                    <div className='ph-medium text-align-right'>
                      {service.serviceType === ServiceType.INDEPENDENT_LIVING ? (
                        <Link
                          to={`/customers/${customerId}/service-agreements/${service.userServiceAgreementId}/service-agreement-service/${service.userServiceAgreementServiceId}`}
                        >
                          <HyperlinkButton>View details...</HyperlinkButton>
                        </Link>
                      ) : (
                        <HyperlinkButton onClick={() => openViewModal(service)}>View details...</HyperlinkButton>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            ))}
        </div>
      </div>
      <div className='mb-x-large'>
        <div className='pb-small'>
          <Text weight='bold'>Quote</Text>
        </div>

        {/* Content Box */}
        <div className='bordered p-medium rounded'>
          <div className='pb-small flex-row justify-between'>
            <FieldLabel text='Quote by service' />
            {!isHistory && hasEditPermission && (
              <HyperlinkButton onClick={() => onClickActionModal(item, 'QUOTE', null)}>
                <Icon type='edit' /> Modify...
              </HyperlinkButton>
            )}
          </div>

          {/* Header */}
          <div className='bordered-bottom pb-medium'>
            <Row gutter={8}>
              <Col span={6}>
                <div className='ph-medium'>
                  <FieldLabel text={'SERVICE NAME'} />
                </div>
              </Col>
              <Col span={4}>
                <div className='ph-medium'>
                  <FieldLabel text={'QUOTE AMOUNT'} />
                </div>
              </Col>
              <Col span={14} />
            </Row>
          </div>

          {item.quotations && item.quotations.length > 0 ? (
            <>
              {_.map(item.quotations, (quote, idx) => {
                return (
                  <Row key={idx} className={'pv-medium evenodd'}>
                    <Col span={6} className={'ph-medium'}>
                      {quote.serviceName}
                    </Col>
                    <Col span={4} className={'text-align-right ph-medium'}>
                      {CommonUtils.formatPrice(quote.quoteAmount)}
                    </Col>
                    <Col span={14} />
                  </Row>
                );
              })}
              <Row key={'TOTAL'} className={'pv-small bordered-top'}>
                <Col span={6} className={'ph-medium'}>
                  <Text weight={'bold'}>Total</Text>
                </Col>
                <Col span={4} className={'text-align-right ph-medium'}>
                  {item.quotations ? (
                    <Text weight={'bold'} style={{ marginRight: '-24px' }}>
                      <HyperlinkButton onClick={() => onClickActionModal(item, 'VIEW_QUOTE', null)}>
                        {CommonUtils.formatPrice(_.sumBy(item.quotations, (item: any) => Number(item.quoteAmount)))}
                        <Icon type={'search'} className={'ml-small'} />
                      </HyperlinkButton>
                    </Text>
                  ) : (
                    0
                  )}
                </Col>
                <Col span={14} />
              </Row>
            </>
          ) : (
            <Row key={'EMPTY'} className={'pv-small bordered-top'}>
              <Col span={24} className={'ph-medium'}>
                <Text color={'secondary'}>
                  No quote created.{!isHistory && hasEditPermission && ` Click on 'Modify' to add a quote.`}
                </Text>
              </Col>
            </Row>
          )}
        </div>
      </div>
      {!isHistory && (
        <>
          {/* Budget tracking */}
          <div className='mb-x-large'>
            <div className='pb-small'>
              <Text weight='bold'>Budget tracking</Text>
            </div>

            {/* Content Box */}
            <div className='bordered p-medium rounded'>
              <div className='pb-small flex-row justify-between'>
                <FieldLabel text='Budget tracking by service' />
              </div>

              {/* Header */}
              <div className='bordered-bottom pb-medium'>
                <Row gutter={8}>
                  <Col span={5}>
                    <div className='ph-medium'>
                      <FieldLabel text={'SERVICE'} />
                    </div>
                  </Col>
                  <Col span={4}>
                    <div className='ph-small text-align-right'>
                      <FieldLabel text={'QUOTE'} />
                    </div>
                  </Col>
                  <Col span={4}>
                    <div className='ph-small text-align-right'>
                      <FieldLabel text={'AMOUNT SPENT'} />
                    </div>
                  </Col>
                  <Col span={4}>
                    <div className='ph-small text-align-right'>
                      <FieldLabel text={'FORECASTED SPEND'} />
                    </div>
                  </Col>
                  <Col span={4}>
                    <div className='ph-small text-align-right'>
                      <FieldLabel text={'ESTIMATED DIFF.'} />
                    </div>
                  </Col>
                  <Col span={3} />
                </Row>
              </div>

              {_.map(item.budgetTracking, (tracking, idx) => {
                return (
                  <Row key={idx} className={'pv-medium evenodd'}>
                    <Col span={5} className={'ph-medium'}>
                      {tracking.serviceName}
                    </Col>
                    <Col span={4} className={'text-align-right ph-small'}>
                      {CommonUtils.formatPrice(tracking.quoteAmount)}
                    </Col>
                    <Col span={4} className={'text-align-right ph-small'}>
                      {CommonUtils.formatPrice(tracking.spentAmount + tracking.amountSpent)}
                    </Col>
                    <Col span={4} className={'text-align-right ph-small'}>
                      {CommonUtils.formatPrice(tracking.forecastedSpend)}
                    </Col>
                    <Col span={4} className={'text-align-right ph-small'}>
                      <Text
                        color={
                          tracking.estimatedDifference > 0
                            ? 'green'
                            : tracking.estimatedDifference < 0
                              ? 'red-dark'
                              : 'black'
                        }
                      >
                        {tracking.estimatedDifference > 0 && '+'}
                        {CommonUtils.formatPrice(tracking.estimatedDifference)}
                      </Text>
                    </Col>
                    <Col span={3} className={'text-align-center'}>
                      {hasEditPermission && (
                        <HyperlinkButton onClick={() => onClickActionModal(item, 'VIEW_TRACKING', tracking)}>
                          View details
                        </HyperlinkButton>
                      )}
                    </Col>
                  </Row>
                );
              })}
              <Row key={'TOTAL'} className={'pv-small bordered-top'}>
                <Col span={5} className={'ph-medium'}>
                  <Text weight={'bold'}>Total</Text>
                </Col>
                <Col span={4} className={'text-align-right ph-small'}>
                  {CommonUtils.formatPrice(totalQuoteAmount)}
                </Col>
                <Col span={4} className={'text-align-right ph-small'}>
                  {CommonUtils.formatPrice(totalAmountSpent)}
                </Col>
                <Col span={4} className={'text-align-right ph-small'}>
                  {CommonUtils.formatPrice(totalForecastedSpend)}
                </Col>
                <Col span={4} className={'text-align-right ph-small'}>
                  <Text color={estimatedDiff > 0 ? 'green' : estimatedDiff < 0 ? 'red-dark' : 'black'}>
                    {estimatedDiff > 0 && '+'}
                    {CommonUtils.formatPrice(estimatedDiff)}
                  </Text>
                </Col>
                <Col span={3} className={'text-align-center'} />
              </Row>
            </div>
          </div>
        </>
      )}
      {!isHistory && hasEditPermission && (
        <div className='mb-small'>
          {/* Created / Edited on */}
          <div className='mb-medium'>
            <Text size='small' color='secondary'>
              Created on {moment(item.createdOn).format('DD/MM/YYYY')}
              {item.updatedOn && `, last edited on ${moment(item.updatedOn).format('DD/MM/YYYY')}`}.
            </Text>
          </div>

          <Paper withBorder p='xs' px='sm' bg='gray.0'>
            <Group gap='md'>
              {item.signedStatus === ServiceAgreementSigningStatus.UNSIGNED && (
                <>
                  <Group gap='xs'>
                    <CreateAgreementLinkButton
                      userServiceAgreementId={item.userServiceAgreementId}
                      customerUserId={customerId}
                    />
                    <MantineButton
                      leftSection={<FileAgreement />}
                      variant='outline'
                      onClick={() => onClickActionModal(item, 'STATUS', ServiceAgreementStatus.SIGNED)}
                    >
                      Manually mark as signed
                    </MantineButton>
                  </Group>
                  <Divider orientation='vertical' />
                </>
              )}
              <Group gap='xs'>
                <DownloadPdfButton serviceAgreement={item} />
                <MantineButton variant='default' leftSection={<History />} onClick={onClickViewVersionHistory}>
                  View version history
                </MantineButton>
              </Group>
              <Divider orientation='vertical' />
              <MantineButton
                leftSection={<Trash />}
                color='red'
                variant='outline'
                onClick={() => onClickActionModal(item, 'DELETE', true)}
              >
                Delete
              </MantineButton>
            </Group>
          </Paper>
        </div>
      )}
    </div>
  );
}
